/**
 * WeaveIn.ai Platform - Signature Pad Component
 * Three modes: Draw (canvas), Type (name → cursive font render), Upload (image)
 * ESIGN Act / UETA compliant: captures intent, consent, identity, timestamp.
 */

import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  PenTool, Type, Upload, Trash2, Check, RotateCcw, X,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

// ============================================================================
// TYPES
// ============================================================================

export interface SignatureResult {
  signature_type: 'drawn' | 'typed' | 'uploaded';
  signature_data: string;          // Base64 PNG
  signature_text: string | null;   // Typed name (for typed mode)
  signature_font: string | null;   // Font used (for typed mode)
  signer_name: string;
  consent_accepted: boolean;
}

interface SignaturePadProps {
  signerName?: string;
  title?: string;
  message?: string;
  consentText?: string;
  onSign: (result: SignatureResult) => void;
  onCancel?: () => void;
  disabled?: boolean;
}

// Signature fonts (Google Fonts, OFL licensed)
const SIGNATURE_FONTS = [
  { name: 'Dancing Script', css: "'Dancing Script', cursive" },
  { name: 'Great Vibes', css: "'Great Vibes', cursive" },
  { name: 'Pacifico', css: "'Pacifico', cursive" },
  { name: 'Caveat', css: "'Caveat', cursive" },
  { name: 'Sacramento', css: "'Sacramento', cursive" },
];

// ============================================================================
// COMPONENT
// ============================================================================

export function SignaturePad({
  signerName = '',
  title = 'Electronic Signature',
  message,
  consentText = 'I agree that this electronic signature is the legal equivalent of my manual signature on this document.',
  onSign,
  onCancel,
  disabled = false,
}: SignaturePadProps) {
  const [mode, setMode] = useState<'draw' | 'type' | 'upload'>('draw');
  const [nameInput, setNameInput] = useState(signerName);
  const [consentAccepted, setConsentAccepted] = useState(false);

  // Draw mode state
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasDrawn, setHasDrawn] = useState(false);

  // Type mode state
  const [typedName, setTypedName] = useState('');
  const [selectedFont, setSelectedFont] = useState(SIGNATURE_FONTS[0]);

  // Upload mode state
  const [uploadedImage, setUploadedImage] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // ========================================================================
  // LOAD GOOGLE FONTS
  // ========================================================================

  useEffect(() => {
    const fontFamilies = SIGNATURE_FONTS.map((f) => f.name.replace(/ /g, '+')).join('&family=');
    const link = document.createElement('link');
    link.href = `https://fonts.googleapis.com/css2?family=${fontFamilies}&display=swap`;
    link.rel = 'stylesheet';
    if (!document.querySelector(`link[href="${link.href}"]`)) {
      document.head.appendChild(link);
    }
  }, []);

  // ========================================================================
  // CANVAS DRAWING
  // ========================================================================

  const initCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width * 2;  // Retina
    canvas.height = rect.height * 2;
    ctx.scale(2, 2);
    ctx.strokeStyle = '#1a1a2e';
    ctx.lineWidth = 2.5;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';

    // Draw signature line
    ctx.beginPath();
    ctx.strokeStyle = '#e5e7eb';
    ctx.lineWidth = 1;
    ctx.moveTo(20, rect.height - 30);
    ctx.lineTo(rect.width - 20, rect.height - 30);
    ctx.stroke();
    ctx.strokeStyle = '#1a1a2e';
    ctx.lineWidth = 2.5;
  }, []);

  useEffect(() => {
    if (mode === 'draw') {
      setTimeout(initCanvas, 50);
    }
  }, [mode, initCanvas]);

  const getPos = (e: React.MouseEvent | React.TouchEvent) => {
    const canvas = canvasRef.current!;
    const rect = canvas.getBoundingClientRect();
    if ('touches' in e) {
      return {
        x: e.touches[0].clientX - rect.left,
        y: e.touches[0].clientY - rect.top,
      };
    }
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  };

  const startDraw = (e: React.MouseEvent | React.TouchEvent) => {
    e.preventDefault();
    const ctx = canvasRef.current?.getContext('2d');
    if (!ctx) return;
    const pos = getPos(e);
    ctx.beginPath();
    ctx.moveTo(pos.x, pos.y);
    setIsDrawing(true);
  };

  const draw = (e: React.MouseEvent | React.TouchEvent) => {
    if (!isDrawing) return;
    e.preventDefault();
    const ctx = canvasRef.current?.getContext('2d');
    if (!ctx) return;
    const pos = getPos(e);
    ctx.lineTo(pos.x, pos.y);
    ctx.stroke();
    setHasDrawn(true);
  };

  const endDraw = () => setIsDrawing(false);

  const clearCanvas = () => {
    setHasDrawn(false);
    initCanvas();
  };

  // ========================================================================
  // TYPED SIGNATURE RENDERING
  // ========================================================================

  const renderTypedSignature = (): string => {
    const canvas = document.createElement('canvas');
    canvas.width = 600;
    canvas.height = 200;
    const ctx = canvas.getContext('2d')!;

    ctx.fillStyle = 'white';
    ctx.fillRect(0, 0, 600, 200);

    // Signature line
    ctx.strokeStyle = '#e5e7eb';
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(20, 150);
    ctx.lineTo(580, 150);
    ctx.stroke();

    // Signature text
    ctx.fillStyle = '#1a1a2e';
    ctx.font = `48px ${selectedFont.css}`;
    ctx.textBaseline = 'bottom';
    ctx.fillText(typedName, 30, 145);

    return canvas.toDataURL('image/png');
  };

  // ========================================================================
  // FILE UPLOAD
  // ========================================================================

  const handleUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setUploadedImage(reader.result as string);
    reader.readAsDataURL(file);
  };

  // ========================================================================
  // SUBMIT
  // ========================================================================

  const canSubmit = () => {
    if (!nameInput.trim() || !consentAccepted) return false;
    if (mode === 'draw') return hasDrawn;
    if (mode === 'type') return typedName.trim().length >= 2;
    if (mode === 'upload') return !!uploadedImage;
    return false;
  };

  const handleSubmit = () => {
    if (!canSubmit()) return;

    let signatureData: string;

    if (mode === 'draw') {
      signatureData = canvasRef.current!.toDataURL('image/png');
    } else if (mode === 'type') {
      signatureData = renderTypedSignature();
    } else {
      signatureData = uploadedImage!;
    }

    onSign({
      signature_type: mode === 'draw' ? 'drawn' : mode === 'type' ? 'typed' : 'uploaded',
      signature_data: signatureData,
      signature_text: mode === 'type' ? typedName : null,
      signature_font: mode === 'type' ? selectedFont.name : null,
      signer_name: nameInput.trim(),
      consent_accepted: true,
    });
  };

  // ========================================================================
  // RENDER
  // ========================================================================

  return (
    <div className="space-y-4">
      {/* Header */}
      <div>
        <h3 className="text-lg font-semibold">{title}</h3>
        {message && <p className="text-sm text-muted-foreground mt-1">{message}</p>}
      </div>

      {/* Signer Name */}
      <div>
        <Label>Full Legal Name *</Label>
        <Input
          value={nameInput}
          onChange={(e) => setNameInput(e.target.value)}
          placeholder="Enter your full name"
          disabled={disabled}
        />
      </div>

      {/* Signature Mode Tabs */}
      <Tabs value={mode} onValueChange={(v) => setMode(v as any)}>
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="draw" className="flex items-center gap-1.5">
            <PenTool className="w-3.5 h-3.5" /> Draw
          </TabsTrigger>
          <TabsTrigger value="type" className="flex items-center gap-1.5">
            <Type className="w-3.5 h-3.5" /> Type
          </TabsTrigger>
          <TabsTrigger value="upload" className="flex items-center gap-1.5">
            <Upload className="w-3.5 h-3.5" /> Upload
          </TabsTrigger>
        </TabsList>

        {/* Draw Tab */}
        <TabsContent value="draw" className="mt-3">
          <div className="relative border-2 border-dashed rounded-lg overflow-hidden bg-white">
            <canvas
              ref={canvasRef}
              className="w-full cursor-crosshair touch-none"
              style={{ height: '160px' }}
              onMouseDown={startDraw}
              onMouseMove={draw}
              onMouseUp={endDraw}
              onMouseLeave={endDraw}
              onTouchStart={startDraw}
              onTouchMove={draw}
              onTouchEnd={endDraw}
            />
            {!hasDrawn && (
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <span className="text-sm text-gray-300">Sign here</span>
              </div>
            )}
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="absolute top-1 right-1"
              onClick={clearCanvas}
            >
              <RotateCcw className="w-3.5 h-3.5" />
            </Button>
          </div>
        </TabsContent>

        {/* Type Tab */}
        <TabsContent value="type" className="mt-3 space-y-3">
          <Input
            value={typedName}
            onChange={(e) => setTypedName(e.target.value)}
            placeholder="Type your name as signature"
            disabled={disabled}
          />

          {/* Font selector */}
          <div className="flex flex-wrap gap-2">
            {SIGNATURE_FONTS.map((font) => (
              <button
                key={font.name}
                type="button"
                className={`px-3 py-2 rounded-md border text-lg transition-colors ${
                  selectedFont.name === font.name
                    ? 'border-blue-500 bg-blue-50 text-blue-900'
                    : 'border-gray-200 hover:border-gray-400'
                }`}
                style={{ fontFamily: font.css }}
                onClick={() => setSelectedFont(font)}
              >
                {typedName || 'Preview'}
              </button>
            ))}
          </div>

          {/* Large preview */}
          {typedName && (
            <div className="border rounded-lg p-4 bg-white text-center">
              <div
                className="text-4xl text-gray-800"
                style={{ fontFamily: selectedFont.css }}
              >
                {typedName}
              </div>
              <div className="border-t border-gray-200 mt-2 pt-1">
                <span className="text-xs text-gray-400">Electronic Signature</span>
              </div>
            </div>
          )}
        </TabsContent>

        {/* Upload Tab */}
        <TabsContent value="upload" className="mt-3">
          <div
            className="border-2 border-dashed rounded-lg p-6 text-center cursor-pointer hover:border-primary/50 transition-colors bg-white"
            onClick={() => fileInputRef.current?.click()}
          >
            {uploadedImage ? (
              <div className="space-y-2">
                <img
                  src={uploadedImage}
                  alt="Signature"
                  className="max-h-24 mx-auto"
                />
                <p className="text-xs text-muted-foreground">Click to replace</p>
              </div>
            ) : (
              <>
                <Upload className="w-8 h-8 mx-auto text-muted-foreground mb-2" />
                <p className="text-sm text-muted-foreground">
                  Upload an image of your signature
                </p>
                <p className="text-xs text-muted-foreground">PNG, JPG, or SVG</p>
              </>
            )}
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/png,image/jpeg,image/svg+xml"
            onChange={handleUpload}
            className="hidden"
          />
        </TabsContent>
      </Tabs>

      {/* Legal Consent */}
      <div className="border rounded-lg p-3 bg-gray-50">
        <label className="flex items-start gap-2.5 cursor-pointer">
          <Checkbox
            checked={consentAccepted}
            onCheckedChange={(v) => setConsentAccepted(!!v)}
            className="mt-0.5"
          />
          <span className="text-xs text-gray-600 leading-relaxed">
            {consentText}
          </span>
        </label>
      </div>

      {/* Actions */}
      <div className="flex justify-end gap-2">
        {onCancel && (
          <Button type="button" variant="outline" onClick={onCancel} disabled={disabled}>
            <X className="w-4 h-4 mr-1" /> Cancel
          </Button>
        )}
        <Button
          type="button"
          onClick={handleSubmit}
          disabled={!canSubmit() || disabled}
          className="bg-v2accent hover:bg-v2accent/90 text-white"
        >
          <Check className="w-4 h-4 mr-1" /> Sign & Submit
        </Button>
      </div>

      {/* Timestamp footer */}
      <p className="text-xs text-gray-400 text-center">
        Signing as <strong>{nameInput || '...'}</strong> on {new Date().toLocaleString()}
      </p>
    </div>
  );
}
